import dayjs from "dayjs";
import "dayjs/locale/th";
import {
  WastePercentCategory,
  WastePercentLines,
} from "../../types/waste-percent-report.type";
import { formatNumber } from "../../utils";
import { UNCATEGORIZED_LABEL } from "./constants";

export type WastePercentLineKey = keyof WastePercentLines;

/** The eight lines of a product or a total block, in the order of the client's Excel sheet. */
export const WASTE_PERCENT_LINES: readonly { key: WastePercentLineKey; label: string }[] = [
  { key: "carryover", label: "ยอดยกมา" },
  { key: "withdraw", label: "ของขึ้น" },
  { key: "remain", label: "ของเหลือ" },
  { key: "waste", label: "ของเสีย" },
  { key: "transfer_in", label: "โอนเข้า" },
  { key: "transfer_out", label: "โอนออก" },
  { key: "sold", label: "ขายได้" },
  { key: "waste_percent", label: "%" },
];

/** The rows one product or one total block spans. */
export const LINES_PER_BLOCK = WASTE_PERCENT_LINES.length;

const PERCENT_LINE_KEY: WastePercentLineKey = "waste_percent";
const PERCENT_DECIMALS = 2;
const QUANTITY_DECIMALS = 2;
/** A real zero, and a percentage the server could not take (no ของขึ้น). */
const DASH = "-";
/** A day with no reconcile data. */
const NO_DATA_TEXT = "";

const THAI_LOCALE = "th";
const WEEKDAY_FORMAT = "dd";
/** dayjs numbers the weekdays from Sunday = 0. */
const SUNDAY = 0;

export interface MonthDay {
  dayNumber: number;
  /** Short Thai weekday, e.g. อา. */
  weekday: string;
  isSunday: boolean;
}

/** The report's day columns, day 1 first, read from the month the server answered for. */
export const monthDays = (month: string, daysInMonth: number): MonthDay[] => {
  const firstDay = dayjs(month).startOf("month").locale(THAI_LOCALE);
  return Array.from({ length: daysInMonth }, (_unused, offset) => {
    const day = firstDay.add(offset, "day");
    return {
      dayNumber: day.date(),
      weekday: day.format(WEEKDAY_FORMAT),
      isSunday: day.day() === SUNDAY,
    };
  });
};

export const isPercentLine = (lineKey: WastePercentLineKey): boolean =>
  lineKey === PERCENT_LINE_KEY;

/** Thousands separators, and decimals only when the figure has them. */
const formatQuantity = (value: number | null): string => {
  if (value === null) return NO_DATA_TEXT;
  if (value === 0) return DASH;
  return formatNumber(value, {
    decimals: Number.isInteger(value) ? 0 : QUANTITY_DECIMALS,
  });
};

const formatPercent = (value: number | null): string =>
  value === null ? DASH : `${formatNumber(value, { decimals: PERCENT_DECIMALS })}%`;

/** How one figure of a line reads in its cell; `undefined` is a day the line does not reach. */
export const formatLineValue = (
  lineKey: WastePercentLineKey,
  value: number | null | undefined,
): string =>
  isPercentLine(lineKey) ? formatPercent(value ?? null) : formatQuantity(value ?? null);

export const categoryLabel = (
  category: Pick<WastePercentCategory, "category_code" | "category_name">,
): string => category.category_name ?? category.category_code ?? UNCATEGORIZED_LABEL;
